"use client";

import { useState } from "react";
import Link from "next/link";
import Image from "next/image";
import { ArrowRight, Images, Calendar } from "lucide-react";
import { formatAlbumDate, type AlbumWithPhotos } from "@/lib/albums";

interface AlbumGridProps {
  albums: AlbumWithPhotos[];
  /** Années scolaires proposées au filtre. Vide ou unique => pas de filtre. */
  schoolYears: string[];
}

export default function AlbumGrid({ albums, schoolYears }: AlbumGridProps) {
  const [activeYear, setActiveYear] = useState<string>("all");

  // Le filtre n'apparaît qu'à partir de deux années scolaires distinctes :
  // en deçà il n'offre aucun choix utile.
  const showFilter = schoolYears.length > 1;

  const visibleAlbums =
    activeYear === "all"
      ? albums
      : albums.filter((album) => album.schoolYear === activeYear);

  return (
    <>
      {showFilter && (
        <div className="flex flex-wrap justify-center gap-3 mb-10">
          <button
            onClick={() => setActiveYear("all")}
            className={`px-5 py-2.5 rounded-full text-sm font-semibold transition-all ${
              activeYear === "all"
                ? "bg-c2e-blue text-white shadow-lg"
                : "bg-white text-gray-700 border-2 border-gray-200 hover:border-c2e-blue"
            }`}
          >
            Tout
          </button>
          {schoolYears.map((year) => (
            <button
              key={year}
              onClick={() => setActiveYear(year)}
              className={`px-5 py-2.5 rounded-full text-sm font-semibold transition-all ${
                activeYear === year
                  ? "bg-c2e-blue text-white shadow-lg"
                  : "bg-white text-gray-700 border-2 border-gray-200 hover:border-c2e-blue"
              }`}
            >
              {year}
            </button>
          ))}
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
        {visibleAlbums.map((album, index) => (
          <Link
            key={album.slug}
            href={`/gallery/${album.slug}`}
            className="group block overflow-hidden rounded-2xl bg-white shadow-md hover:shadow-2xl transition-all duration-300 hover:-translate-y-1 focus:outline-none focus:ring-2 focus:ring-c2e-blue focus:ring-offset-2"
          >
            {/* Hauteur explicite plutôt qu'aspect-ratio + fill : l'image
                garde ses dimensions intrinsèques et le conteneur ne peut pas
                s'effondrer, quel que soit le rendu du navigateur. */}
            <div className="relative h-52 sm:h-56 overflow-hidden bg-gray-100">
              <Image
                src={album.coverPhoto.src}
                alt={album.title}
                width={album.coverPhoto.width}
                height={album.coverPhoto.height}
                sizes="(min-width: 1024px) 400px, (min-width: 640px) 50vw, 92vw"
                loading={index < 3 ? "eager" : "lazy"}
                className="h-52 sm:h-56 w-full object-cover transition-transform duration-500 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />

              {/* Compteur de photos */}
              <div className="absolute top-3 right-3 flex items-center gap-1.5 rounded-full bg-black/50 backdrop-blur-sm px-3 py-1.5 text-white text-xs font-semibold">
                <Images className="h-3.5 w-3.5" />
                {album.photos.length}
              </div>

              <div className="absolute bottom-0 left-0 right-0 p-4">
                <h3 className="text-white text-lg font-bold drop-shadow-lg leading-snug">
                  {album.title}
                </h3>
                {album.date && (
                  <p className="mt-1 flex items-center gap-1.5 text-white/85 text-xs">
                    <Calendar className="h-3.5 w-3.5" />
                    {formatAlbumDate(album.date)}
                  </p>
                )}
              </div>
            </div>

            <div className="p-5">
              <p className="text-sm text-gray-600 leading-relaxed line-clamp-3">
                {album.description}
              </p>
              <span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-c2e-blue group-hover:gap-2 transition-all">
                Ouvrir l&apos;album
                <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </span>
            </div>
          </Link>
        ))}
      </div>

      {visibleAlbums.length === 0 && (
        <p className="text-center text-gray-600 py-12">
          Aucun album pour cette année.
        </p>
      )}
    </>
  );
}
